import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraRangeDotPlot } from '@mono/ui-components/captains';
import { SpecraLink, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraInlineBanner, SpecraSegmentedControl } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { PointTypeOrder, plotAxis, plotRows, pointStyle, pointTitle, usd } from './model';
const BURDEN_WARNING = 'BURDEN_NOT_STATED';
const WARNING_TEXT = {
    [BURDEN_WARNING]: "Some points don't state a burden basis, so they can't be compared with the band."
};
const warningText = (w)=>WARNING_TEXT[w] ?? `${w.charAt(0)}${w.slice(1).toLowerCase().replace(/_/g, ' ')}`;
export function RateDotPlot({ data, anchorId, fy, currentFy, onFyChange }) {
    const { suggestion, benchmark } = data;
    const rows = plotRows(data);
    const values = [
        ...data.lanes.flatMap((l)=>l.points.map((p)=>p.amount)),
        ...suggestion ? [
            suggestion.low,
            suggestion.high
        ] : [],
        ...benchmark ? [
            benchmark.amount
        ] : []
    ];
    const { domain, ticks } = plotAxis(values);
    const legend = [
        ...PointTypeOrder.map((type)=>{
            const s = pointStyle(type);
            return {
                label: s.label,
                tone: s.tone,
                shape: s.shape
            };
        }),
        ...benchmark ? [
            {
                label: 'CALC+ median',
                tone: 'pending',
                shape: 'dashed-line'
            }
        ] : [],
        ...suggestion ? [
            {
                label: 'suggested for pricing',
                tone: 'ours',
                shape: 'band'
            }
        ] : []
    ];
    const noBand = !suggestion && data.lanes.length > 0 && data.warnings.includes(BURDEN_WARNING);
    const otherWarnings = data.warnings.filter((w)=>!(noBand && w === BURDEN_WARNING));
    const excluded = data.excludedNotComparable;
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsx(SpecraRangeDotPlot, {
                "data-testid": Testids.registryLcatAnchor.dotPlot,
                title: "What this work actually costs · hourly, by company",
                actions: /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                    size: "sm",
                    label: "Fiscal year",
                    testId: Testids.registryLcatAnchor.fyToggle,
                    value: fy,
                    onValueChange: onFyChange,
                    options: [
                        {
                            value: 'prev',
                            label: `FY${String(currentFy - 1).slice(-2)}`
                        },
                        {
                            value: 'current',
                            label: `FY${String(currentFy).slice(-2)}`
                        },
                        {
                            value: 'both',
                            label: 'Both years'
                        }
                    ]
                }),
                rows: rows,
                domain: domain,
                ticks: ticks,
                band: suggestion ? {
                    from: suggestion.low,
                    to: suggestion.high,
                    label: 'suggested for pricing'
                } : undefined,
                reference: benchmark ? {
                    value: benchmark.amount,
                    label: 'CALC+ median'
                } : undefined,
                legend: legend,
                emptyMessage: "No comparable hourly rates for this anchor yet.",
                insight: suggestion ? /*#__PURE__*/ _jsxs("span", {
                    "data-testid": Testids.registryLcatAnchor.suggestedBand,
                    children: [
                        "Suggested ",
                        usd(suggestion.low),
                        "–",
                        usd(suggestion.high),
                        "/hr: for the last two years we use the lowest known real rate (agreed, billed or awarded); for future periods, the ceiling. Every point keeps its type, period as written, and source. Hover any point."
                    ]
                }) : undefined
            }),
            noBand && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "neutral",
                variant: "framed",
                "data-testid": Testids.registryLcatAnchor.noBandBanner,
                action: /*#__PURE__*/ _jsx(SpecraLink, {
                    href: `/registries/review-queue?kind=BURDEN_BASIS&anchor=${encodeURIComponent(anchorId)}`,
                    renderLink: renderRouterLink,
                    children: "Open the review queue"
                }),
                children: "No suggested band: the source sheets don't state a burden basis. State it in the review queue."
            }),
            otherWarnings.map((w)=>/*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "warning",
                    variant: "framed",
                    "data-testid": Testids.registryLcatAnchor.warning,
                    children: warningText(w)
                }, w)),
            (data.unalignedPoints.length > 0 || excluded > 0) && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                "data-testid": Testids.registryLcatAnchor.unalignedNote,
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "meta",
                        tone: "muted",
                        children: [
                            data.unalignedPoints.length > 0 && `${data.unalignedPoints.length} ${data.unalignedPoints.length === 1 ? 'point has' : 'points have'} no aligned period, so ${data.unalignedPoints.length === 1 ? 'it is' : 'they are'} listed here instead of plotted`,
                            excluded > 0 && `${excluded} ${excluded === 1 ? 'point is' : 'points are'} not hourly (or differ in burden basis or currency) and ${excluded === 1 ? 'is' : 'are'} not plotted`
                        ].filter(Boolean).join(' · ')
                    }),
                    data.unalignedPoints.length > 0 && /*#__PURE__*/ _jsx("ul", {
                        className: "list-disc pl-5",
                        children: data.unalignedPoints.map((p)=>/*#__PURE__*/ _jsx("li", {
                                children: /*#__PURE__*/ _jsx(SpecraText, {
                                    size: "meta",
                                    tone: "muted",
                                    children: pointTitle(p)
                                })
                            }, p.observationId))
                    })
                ]
            })
        ]
    });
}
